import { useCallback, useEffect, useState } from "react";
import { HdgTextInput } from "../../components/hdg-text-input";
import {
  useUserContext,
  useUserContextDispatch,
} from "../../hooks/userContext";
import { Button, Stack, Fade, Zoom, CircularProgress } from "@mui/material";
import "./styles.scss";
import { Link, useNavigate } from "react-router";
import { DualScreenLayout } from "../../components/dual-screen-layout";
import { ErrorMessage } from "../../components/error-message";
import { isEmailValid, isPasswordValid, Pages } from "../../utils";
import { LoginPageDecorativeContent } from "./login-page-decorative-content";
import { useNotification } from "../../contexts/NotificationContext";
import { PaymentTokenManager } from "../../services/paymentApi";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  getRegistrationProgressStep,
  setRegistrationResumeIntent,
} from "../../utils/registration-progress";

const getEmailVerifiedFlag = (value: unknown): boolean | null => {
  const normalized = String(value ?? "").toLowerCase();
  if (normalized === "true") return true;
  if (normalized === "false") return false;
  return null;
};

export const LoginPage = () => {
  const { loading, user } = useUserContext();
  const { loginUserAccount, logoutUserAccount } = useUserContextDispatch();
  const navigate = useNavigate();
  const { showLoginError, showSuccess, showError } = useNotification();
  const mainWebsiteUrl = "https://homedesignguild.com";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [hasAttemptedLogin, setHasAttemptedLogin] = useState(false);

  const isValid =
    email && password && isEmailValid(email) && password.length > 5;
  const shouldLeftCollapse = window.innerWidth <= 580;

  const handleLoggedInUserDestination = useCallback((currentUser: any) => {
    if (!currentUser) return;

    if ((currentUser.accountType as string) === "Admin") {
      navigate(Pages.ADMIN_DASHBOARD);
      return;
    }

    const onboardingStatus =
      currentUser.onboardingStatus || "pending_email_verification";
    const userType = currentUser.accountType || "Homeowner";
    const emailVerifiedFlag = getEmailVerifiedFlag(currentUser.emailVerified);

    if (onboardingStatus === "active") {
      navigate(Pages.DASHBOARD);
      return;
    }

    if (
      (onboardingStatus === "pending_profile_completion" ||
        onboardingStatus === "profile_completed") &&
      currentUser.accountType === "Designer"
    ) {
      // Designers continue from dashboard application flow.
      navigate(Pages.DASHBOARD);
      return;
    }

    let resumeStep: number | null = null;

    if (
      emailVerifiedFlag === false ||
      onboardingStatus === "pending_email_verification"
    ) {
      resumeStep = 2;
    } else {
      const storedProgressStep = getRegistrationProgressStep(currentUser);
      resumeStep =
        storedProgressStep !== null && storedProgressStep > 0
          ? storedProgressStep
          : null;

      if (resumeStep === null) {
        if (userType === "Homeowner" && onboardingStatus === "pending_profile_completion") {
          const paymentData = PaymentTokenManager.getPaymentData();
          const userPaymentStatus = (currentUser as any).paymentStatus;
          resumeStep = paymentData || userPaymentStatus === "paid" ? 5 : 4;
        } else if (userType === "Designer") {
          resumeStep = 3;
        } else {
          resumeStep = 1;
        }
      }
    }

    sessionStorage.setItem("resumeRegistrationUser", JSON.stringify(currentUser));
    setRegistrationResumeIntent();
    navigate(
      `${Pages.SIGNUP}?userType=${userType}&step=${resumeStep}&resume=true`
    );
  }, [navigate]);

  useEffect(() => {
    // Only redirect if user just logged in (not if they navigated to login page while logged in)
    if (user && hasAttemptedLogin) {
      handleLoggedInUserDestination(user);
    }
  }, [user, hasAttemptedLogin, handleLoggedInUserDestination]);

  const handleLogin = async () => {
    setIsLoggingIn(true);
    setError(null);
    setHasAttemptedLogin(true); // Mark that user is attempting to log in
    
    try {
      const loginResult = await loginUserAccount({ email, password });
      
      if (loginResult?.error) {
        const errorMessage = loginResult.error as string;
        
        // Handle specific error types with appropriate messages and actions
        if (errorMessage.toLowerCase().includes('user not found') || 
            errorMessage.toLowerCase().includes('invalid email')) {
          showLoginError(
            `No account found with email "${email}". Please check your email or create an account.`,
            () => navigate(Pages.SIGNUP)
          );
        } else if (errorMessage.toLowerCase().includes('invalid password') || 
                   errorMessage.toLowerCase().includes('password')) {
          showLoginError(
            'Incorrect password. Please check your password and try again.',
          );
        } else if (errorMessage.toLowerCase().includes('failed to login')) {
          showError(
            'Login failed. Please check your credentials and try again.'
          );
        } else {
          showLoginError(errorMessage);
        }
        
        setError(errorMessage);
      } else {
        // Success case - show success message briefly before redirect
        showSuccess('Welcome back! Redirecting to your dashboard...');
      }
    } catch (error: any) {
      showError('An unexpected error occurred. Please try again.');
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  // If user is already logged in and navigated here, show option to continue or logout
  if (user && !hasAttemptedLogin) {
    return (
      <div className="page login-page">
        <DualScreenLayout
          collapseLeft={shouldLeftCollapse}
          left={
            <LoginPageDecorativeContent>
              <div className="text-container">
                <h1 className="font-title font-size-7">
                  Welcome Back!
                </h1>
                <p className="font-size-4">
                  You're already logged in.
                </p>
              </div>
            </LoginPageDecorativeContent>
          }
          right={
            <Stack className="login-form-wrapper" alignItems="center">
              <div className="content">
                <Button
                  className="login-page-back-button"
                  startIcon={<ArrowBackIcon />}
                  component="a"
                  href={mainWebsiteUrl}
                  variant="text"
                >
                  Back
                </Button>
                <Fade in={true} timeout={800}>
                  <div>
                    <h1 className="font-title font-size-6">Already Logged In</h1>
                    <p style={{ marginTop: '16px', color: '#666' }}>
                      You're currently logged in as <strong>{user.email}</strong>
                    </p>
                  </div>
                </Fade>
                
                <Fade in={true} timeout={1000} style={{ transitionDelay: '200ms' }}>
                  <Stack className="login-container" direction="column" spacing={3} sx={{ mt: 3 }}>
                    <Button
                      className="login-button"
                      size="large"
                      onClick={() => handleLoggedInUserDestination(user)}
                      variant="contained"
                      color={"black" as any}
                      sx={{
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 3,
                        },
                      }}
                    >
                      {(user.accountType as string) === 'Admin' || user.onboardingStatus === 'active' ? 'Go to Dashboard' : 'Complete Registration'}
                    </Button>
                    
                    <Button
                      size="large"
                      onClick={() => {
                        logoutUserAccount();
                        setEmail("");
                        setPassword("");
                      }}
                      variant="outlined"
                      sx={{
                        borderColor: '#000',
                        color: '#000',
                        '&:hover': {
                          borderColor: '#333',
                          backgroundColor: 'rgba(0,0,0,0.04)',
                        },
                      }}
                    >
                      Log Out & Sign In as Different User
                    </Button>
                  </Stack>
                </Fade>
              </div>
            </Stack>
          }
        />
      </div>
    );
  }

  return (
    <div className="page login-page">
      <DualScreenLayout
        collapseLeft={shouldLeftCollapse}
        left={
          <LoginPageDecorativeContent>
            <div className="text-container">
              <h1 className="font-title font-size-7">
                Welcome to
                <br />
                Home Design Guild!
              </h1>
              <p className="font-size-4">
                Find, Create, and Collaborate on Beautiful Designs.
              </p>
            </div>
          </LoginPageDecorativeContent>
        }
        right={
          <Stack className="login-form-wrapper" alignItems="center">
            <div className="content">
              <Button
                className="login-page-back-button"
                startIcon={<ArrowBackIcon />}
                component="a"
                href={mainWebsiteUrl}
                variant="text"
              >
                Back
              </Button>
              <Fade in={true} timeout={800}>
                <div>
                  <h1 className="font-title font-size-6">Sign In</h1>
                </div>
              </Fade>
              
              <Fade in={true} timeout={1000} style={{ transitionDelay: '200ms' }}>
                <Stack className="login-container" direction="column" spacing={3}>
                  <HdgTextInput
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(value) => setEmail(value)}
                  />
                  <HdgTextInput
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(value) => setPassword(value)}
                  />
                  
                  <Zoom in={true} timeout={600} style={{ transitionDelay: '400ms' }}>
                    <Button
                      className="login-button"
                      size="large"
                      disabled={!isValid || loading || isLoggingIn}
                      onClick={handleLogin}
                      variant="contained"
                      color={"black" as any}
                      startIcon={isLoggingIn ? <CircularProgress size={20} sx={{ color: '#00A06D' }} /> : undefined}
                      sx={{
                        position: 'relative',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 3,
                        },
                        '&:active': {
                          transform: 'translateY(0px)',
                        },
                      }}
                    >
                      {isLoggingIn ? 'Signing In...' : 'Login'}
                    </Button>
                  </Zoom>
                  
                  <Fade in={true} timeout={800} style={{ transitionDelay: '600ms' }}>
                    <Stack
                      direction="row"
                      justifyContent="center"
                      spacing={5}
                      alignItems="center"
                    >
                      <Link
                        className="link forgot-password-link"
                        to={Pages.FORGOT_PASSWORD}
                      >
                        Forgot Password?
                      </Link>
                      <Link className="link registration-link" to={Pages.SIGNUP}>
                        Create an Account
                      </Link>
                    </Stack>
                  </Fade>
                </Stack>
              </Fade>
            </div>
          </Stack>
        }
      />
    </div>
  );
};
